import React, { useEffect, useState } from 'react';
import { Award, Users, Star, CheckCircle, Clock, Eye, Heart, Crown, Lightbulb } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import Logo from '../components/Logo';
import AnimatedCounter from '../components/AnimatedCounter';

const About = () => {
  useEffect(() => {
    document.title = "About Ceballos Total Framing - Arizona's Premier Structural Specialists | 25+ Years Excellence";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover Ceballos Total Framing\'s journey from vision to Arizona\'s most trusted luxury framing specialists. Serving Paradise Valley, Scottsdale, and Arcadia\'s finest custom homes since 2018.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      
      {/* Architectural Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-72">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/ParadiseValley_McIntyreDevelopmentPatio.jpg" 
            alt="About Hero Background - Paradise Valley McIntyre Development Patio"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.5) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gray-900/40"></div>
        </div>

        <div className="relative z-20 text-center text-white px-6 max-w-8xl mx-auto">
          <AnimatedSection delay={200}>
            <div className="text-center">
              <div className="inline-block mb-8">
                
              </div>
              <h1 className="text-5xl md:text-7xl font-light mb-8 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Vision</span>
                <br />to Excellence
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                The story of Arizona's most distinguished structural framing specialists
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Building Arizona's <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Legacy</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <AnimatedSection delay={400}>
              <div className="space-y-8">
                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>The Vision</h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                    In 2018, Aureliano Ceballos founded Ceballos Total Framing with a singular vision: to become Arizona's most trusted name in luxury structural framing. Drawing from over two decades of individual expertise beginning in 2000, our company was built on the principle that exceptional homes deserve exceptional foundations.
                  </p>
                </div>

                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                      <Heart className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>The Passion</h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                    What began as a commitment to structural excellence has evolved into Arizona's premier destination for exclusive builders. Every project reflects our unwavering dedication to precision framing, superior materials, and the timeless craftsmanship that defines true luxury construction.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="bg-gray-900 p-12 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                <div className="mb-8 text-center">
                  <Logo size="large" />
                </div>
                
                {/* Clean Timeline Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* 2000 - Journey Begins */}
                  <div className="group">
                    <div className="bg-gray-800 p-8 border border-amber-400/20 rounded-xl hover:border-amber-400/40 transition-all duration-500 h-full group-hover:shadow-2xl group-hover:shadow-amber-500/20 flex flex-col">
                      <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                        2000
                      </div>
                      <h3 className="text-xl font-medium text-white mb-3 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Individual Journey Begins</h3>
                      <p className="text-gray-300 font-light leading-relaxed flex-grow" style={{ fontFamily: 'Inter, sans-serif' }}>Aureliano's framing expertise development and mastery of structural techniques</p>
                    </div>
                  </div>

                  {/* 2018 - Company Foundation */}
                  <div className="group">
                    <div className="bg-gray-800 p-8 border border-amber-400/20 rounded-xl hover:border-amber-400/40 transition-all duration-500 h-full group-hover:shadow-2xl group-hover:shadow-amber-500/20 flex flex-col">
                      <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                        2018
                      </div>
                      <h3 className="text-xl font-medium text-white mb-3 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Company Foundation</h3>
                      <p className="text-gray-300 font-light leading-relaxed flex-grow" style={{ fontFamily: 'Inter, sans-serif' }}>Ceballos Total Framing LLC established with a vision for excellence</p>
                    </div>
                  </div>

                  {/* Today - Arizona's Premier */}
                  <div className="group">
                    <div className="bg-gray-800 p-8 border border-amber-400/20 rounded-xl hover:border-amber-400/40 transition-all duration-500 h-full group-hover:shadow-2xl group-hover:shadow-amber-500/20 flex flex-col">
                      <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Today
                      </div>
                      <h3 className="text-xl font-medium text-white mb-3 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Arizona's Premier</h3>
                      <p className="text-gray-300 font-light leading-relaxed flex-grow" style={{ fontFamily: 'Inter, sans-serif' }}>Luxury structural specialists serving the state's most exclusive projects</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Expertise & Values Section */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                What Defines <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Excellence</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-800 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 h-full" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Uncompromising Quality</h3>
                </div>
                <p className="text-gray-300 leading-relaxed font-light mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Every project reflects our commitment to the highest standards of structural integrity and precision.
                </p>
                <ul className="space-y-3 text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Premium materials exclusively</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Rigorous quality control</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Zero compromise standards</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="bg-gray-800 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 h-full" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Client Partnership</h3>
                </div>
                <p className="text-gray-300 leading-relaxed font-light mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We believe in collaborative relationships that transform architectural visions into structural reality for our clients.
                </p>
                <ul className="space-y-3 text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Direct communication</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Transparent processes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Exceeding expectations</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="bg-gray-800 p-8 border border-amber-400/20 hover:border-amber-400/40 transition-all duration-500 h-full" style={{ borderRadius: '12px' }}>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center mr-6" style={{ borderRadius: '8px' }}>
                    <Lightbulb className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Innovation & Tradition</h3>
                </div>
                <p className="text-gray-300 leading-relaxed font-light mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Combining time-tested techniques with cutting-edge technology for superior structural solutions in every project.
                </p>
                <ul className="space-y-3 text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Advanced engineering</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Proven methodologies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-amber-400 mr-3 mt-1 flex-shrink-0" />
                    <span className="font-light">Continuous improvement</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Meet <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Aureliano</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <AnimatedSection delay={400}>
              <div className="bg-gray-900 p-12 border border-amber-400/20 text-center" style={{ borderRadius: '12px' }}>
                <div className="w-32 h-32 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full mx-auto mb-8 flex items-center justify-center">
                  <span className="text-3xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>AC</span>
                </div>
                <h3 className="text-3xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Aureliano Ceballos</h3>
                <p className="text-xl text-amber-400 mb-6 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Founder & Master Craftsman</p>
                <div className="space-y-4 text-gray-300 text-left">
                  <div className="flex items-center">
                    <Award className="h-5 w-5 text-amber-400 mr-3" />
                    <span className="font-light" style={{ fontFamily: 'Inter, sans-serif' }}>CR-7 Licensed Professional</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-amber-400 mr-3" />
                    <span className="font-light" style={{ fontFamily: 'Inter, sans-serif' }}>25+ Years Experience</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-amber-400 mr-3" />
                    <span className="font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Zero Structural Failures</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="space-y-8">
                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <h4 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Philosophy</h4>
                  <p className="text-gray-300 leading-relaxed font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
                    "Every structure tells a story. My role is to ensure that story stands for generations, built on a foundation of integrity, precision, and unwavering commitment to excellence."
                  </p>
                </div>

                <div className="bg-gray-900 p-8 border border-amber-400/20" style={{ borderRadius: '12px' }}>
                  <h4 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Expertise</h4>
                  <p className="text-gray-300 leading-relaxed font-light mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                    With over two decades of hands-on experience in luxury residential framing, Aureliano brings unmatched expertise to every project. His deep understanding of both traditional craftsmanship and modern engineering ensures structures that are both beautiful and built to last.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <div>• Wood Framing Mastery</div>
                    <div>• Steel Structural</div>
                    <div>• Engineering Understanding</div>
                    <div>• Project Leadership</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Credentials & Achievements */}
      <section className="py-32 bg-gray-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center mb-20">
              <div className="inline-block mb-8">
                
              </div>
              <h2 className="text-4xl md:text-6xl font-light mb-12 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Proven <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Excellence</span>
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            
            <AnimatedSection delay={400}>
              <div className="text-center">
                <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <AnimatedCounter end={25} />+
                </div>
                <div className="text-lg font-light text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Projects Completed</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Luxury homes across Arizona</div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <div className="text-center">
                <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  100%
                </div>
                <div className="text-lg font-light text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Client Satisfaction</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Zero warranty claims</div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="text-center">
                <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <AnimatedCounter end={12} />M+
                </div>
                <div className="text-lg font-light text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Project Values</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Completed successfully</div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={1000}>
              <div className="text-center">
                <div className="text-4xl font-light bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  CR-7
                </div>
                <div className="text-lg font-light text-gray-300 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Licensed & Insured</div>
                <div className="text-sm text-gray-400 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>Fully bonded professional</div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-32 bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent"></div>
        
        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative">
          
          <AnimatedSection delay={200}>
            <div className="text-center">
              <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ready to Build Your <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Legacy</span>?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Experience the difference that comes from working with Arizona's most trusted structural specialists.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a 
                  href="/contact"
                  className="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-amber-600 text-white px-12 py-4 transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-amber-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ borderRadius: '8px' }}></div>
                  <span className="relative z-10 flex items-center">
                    Discuss Your Project
                  </span>
                </a>
                
                <a 
                  href="tel:602-448-1723"
                  className="group border border-amber-400/60 text-white px-12 py-4 hover:bg-amber-400/10 transition-all duration-500 backdrop-blur-md shadow-xl text-lg font-medium tracking-wide"
                  style={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}
                >
                  <span className="flex items-center">
                    Call Today
                  </span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default About;